'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@ps/ui/utils';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';

import type { ComposerAttachment } from './useComposerAttachments';

interface Props {
  attachments: ComposerAttachment[];
  onRemove: (id: string) => void;
}

/**
 * Horizontal scroll strip rendered below the editor when the user
 * has staged one or more attachments. Each tile is a square preview
 * of the local file (image or video first frame) with an X-close
 * button that appears on hover.
 *
 * Chevron arrows fade in only when the strip overflows — measured
 * via a scroll listener on the strip element. On click, they nudge
 * the scroll position by one tile width.
 */
export function ChannelComposerAttachmentStrip({
  attachments,
  onRemove,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft < maxScroll - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [attachments.length]);

  if (attachments.length === 0) return null;

  const nudge = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 140, behavior: 'smooth' });
  };

  return (
    <div className="relative mt-2">
      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {attachments.map(item => (
          <ChannelComposerAttachmentTile
            key={item.id}
            attachment={item}
            onRemove={() => onRemove(item.id)}
          />
        ))}
      </div>
      {canScrollLeft ? (
        <ScrollNudgeButton direction="left" onClick={() => nudge(-1)} />
      ) : null}
      {canScrollRight ? (
        <ScrollNudgeButton direction="right" onClick={() => nudge(1)} />
      ) : null}
    </div>
  );
}

function ChannelComposerAttachmentTile({
  attachment,
  onRemove,
}: {
  attachment: ComposerAttachment;
  onRemove: () => void;
}) {
  const isVideo = attachment.contentType.startsWith('video/');
  return (
    <div className="group relative h-[120px] w-[120px] shrink-0 overflow-visible">
      <div className="bg-surface-1 absolute inset-0 overflow-hidden rounded-md">
        {isVideo ? (
          <>
            <video
              src={attachment.localPreviewUrl}
              className="h-full w-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
            <div className="bg-scrim/30 absolute inset-0 flex items-center justify-center">
              <Play className="h-7 w-7 fill-white text-white" aria-hidden />
            </div>
          </>
        ) : (
          <img
            src={attachment.localPreviewUrl}
            alt={attachment.filename}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <button
        type="button"
        aria-label={`Remove ${attachment.filename}`}
        onClick={onRemove}
        className="text-content-1 bg-surface-1 absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full opacity-0 shadow-lg transition focus-visible:opacity-100 group-hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

function ScrollNudgeButton({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-md transition hover:scale-105',
        direction === 'left' ? 'left-1' : 'right-1',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
